import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useDebouncedValue } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Center,
  Checkbox,
  Code,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconRoute, IconSearch } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import { listSerials, type SerialDto } from './api';

/**
 * Serials.
 *
 * **On hand first.** The usual reason to open this screen is to find a unit that is supposed to
 * be in the building; a unit that shipped last March is the other question, and it sorts below.
 *
 * **Every row says where the unit is OR who has it, never neither.** A serial is one physical
 * thing, so "somewhere" and "somebody" are the only two states it can be in, and both are worked
 * out from the ledger rather than read from a stored field — there is no status column on a
 * serial and 088 explains at length why there should not be.
 *
 * **No register button.** A serial comes into existence by being scanned onto a receipt or an
 * adjustment. Legacy has no serial master at all, let alone a maintenance screen, and a serial
 * number typed into a form would be a unit nobody has ever held.
 */
export function SerialsRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const warehouseId = useCurrentWarehouseId();
  const [search, setSearch] = useState('');
  const [onHandOnly, setOnHandOnly] = useState(false);

  // Asked of the server, like lots and for the same reason, only more so: there is one row per
  // physical unit, so a tenant that serializes anything high-volume has more serials than
  // everything else in the database put together. The checkbox is not debounced — a click is
  // already a finished thought.
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const query = useQuery({
    queryKey: ['serials', warehouseId, debouncedSearch, onHandOnly] as const,
    queryFn: ({ signal }) => listSerials(
      { search: debouncedSearch.trim() || null, onHandOnly }, {}, signal,
    ),
    enabled: Boolean(warehouseId),
  });

  const rows = query.data?.items ?? [];

  const columns: DataColumn<SerialDto>[] = useMemo(() => [
    {
      key: 'serialNumber',
      label: t('serials.serialNumber'),
      card: 'title',
      render: (s) => <Code>{s.serialNumber}</Code>,
    },
    {
      key: 'item',
      label: t('serials.item'),
      card: 'subtitle',
      render: (s) => (
        <Stack gap={0}>
          <Text size="sm">{s.itemNumber}</Text>
          {s.itemDescription ? <Text size="xs" c="dimmed">{s.itemDescription}</Text> : null}
        </Stack>
      ),
    },
    {
      key: 'where',
      label: t('serials.where'),
      card: 'aside',
      // The column that is the whole point of the screen. On hand: the aisle, and the plate if
      // it is on one. Gone: the customer, because "not here" on its own sends somebody to look
      // for it.
      render: (s) => {
        if (s.onHandQuantity > 0) {
          return (
            <Stack gap={0}>
              <Badge size="sm" color="teal" variant="light">
                {s.currentLocationCode ?? t('serials.somewhereHere')}
              </Badge>
              {s.currentLicensePlateNumber
                ? <Text size="xs" c="dimmed">{t('serials.onPlate', { plate: s.currentLicensePlateNumber })}</Text>
                : null}
            </Stack>
          );
        }
        if (s.customerName) {
          return (
            <Stack gap={0}>
              <Badge size="sm" color="gray" variant="light">{t('serials.shipped')}</Badge>
              <Text size="xs" c="dimmed">{s.customerName}</Text>
            </Stack>
          );
        }
        // Neither on hand nor shipped: adjusted out, scrapped, or counted away. Saying "gone"
        // is honest; inventing a reason would not be.
        return <Badge size="sm" color="gray" variant="light">{t('serials.gone')}</Badge>;
      },
    },
    {
      key: 'lot',
      label: t('serials.lot'),
      render: (s) => (s.lotNumber ? <Code>{s.lotNumber}</Code> : <Text size="sm" c="dimmed">—</Text>),
    },
    {
      key: 'lastSeen',
      label: t('serials.lastMovement'),
      render: (s) => (s.lastMovementAtUtc
        ? <Text size="sm">{new Date(s.lastMovementAtUtc).toLocaleDateString()}</Text>
        : <Text size="sm" c="dimmed">—</Text>),
    },
  ], [t]);

  if (!warehouseId) {
    return (
      <Stack>
        <PageHeader title={t('nav.serials')} />
        <Alert icon={<IconAlertCircle size={16} />} color="yellow">
          {t('serials.chooseWarehouse')}
        </Alert>
      </Stack>
    );
  }

  return (
    <>
      <PageHeader title={t('nav.serials')} />

      <Stack gap="md">
        <Text size="sm" c="dimmed">{t('serials.intro')}</Text>

        <Group align="flex-end">
          <TextInput
            label={t('serials.search')}
            description={t('serials.searchHint')}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={{ base: '100%', sm: 320 }}
          />
          <Checkbox
            label={t('serials.onHandOnly')}
            checked={onHandOnly}
            onChange={(e) => setOnHandOnly(e.currentTarget.checked)}
            pb={8}
          />
        </Group>

        {query.isLoading && <Center py="xl"><Loader color="invetrak" /></Center>}

        {query.isError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {query.error instanceof ApiError
              ? query.error.problem.title
              : t('serials.errors.loadFailed')}
          </Alert>
        )}

        {query.isSuccess && rows.length === 0 && (
          <Alert color="gray" variant="light">
            {search.trim() ? t('serials.noMatch') : t('serials.empty')}
          </Alert>
        )}

        {query.isSuccess && rows.length > 0 && (
          <>
            <ResponsiveTable
              rows={rows}
              columns={columns}
              rowKey={(s) => s.serialId}
              minWidth={720}
              actionsWidth={64}
              striped
              highlightOnHover
              actions={(s) => (
                <ActionIcon
                  variant="subtle"
                  color="invetrak"
                  aria-label={t('serials.openHistory', { serial: s.serialNumber })}
                  onClick={() => void navigate({
                    to: '/app/serials/$id', params: { id: s.serialId },
                  })}
                >
                  <IconRoute size={16} />
                </ActionIcon>
              )}
            />
            {/* A page out of thousands is not the whole answer, and a grid that silently shows
                the first page is how somebody concludes a unit is missing. */}
            {query.data!.totalCount > rows.length && (
              <Text size="xs" c="dimmed">
                {t('serials.showing', { shown: rows.length, total: query.data!.totalCount })}
              </Text>
            )}
          </>
        )}
      </Stack>
    </>
  );
}
